import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const linkClass =
  "group relative text-[13px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 hover:text-brand";

const Underline = () => (
  <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand transition-[width] duration-300 group-hover:w-full" />
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const solid = scrolled || pathname === "/contact" || pathname === "/admin" || pathname === "/clientele";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,color] duration-500 ${
          solid
            ? "border-b border-ink/10 bg-white/85 text-ink backdrop-blur-xl"
            : "border-b border-white/10 bg-black/25 text-white backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex h-20 w-full max-w-[1400px] items-center justify-between px-6 sm:px-10 lg:px-16">
          <Link to="/" data-testid="nav-brand" className="flex items-center gap-3">
            <img src="/assets/logo-mark.png" alt="Devkii Electricals logo" className="h-10 w-auto rounded-md" />
            <span className="leading-none">
              <span className="block font-heading text-lg font-bold tracking-tight">Devkii Electricals</span>
              <span className="mt-1 block text-[9px] font-medium uppercase tracking-[0.3em] opacity-60">
                Known for Innovations
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-10 lg:flex" data-testid="nav-desktop">
            <Link to="/" data-testid="nav-home" className={`${linkClass} ${pathname === "/" ? "text-brand" : ""}`}>
              Home
              <Underline />
            </Link>
            <div className="group relative" data-testid="nav-company">
              <button
                data-testid="nav-company-trigger"
                className={`${linkClass} flex items-center gap-1.5`}
              >
                Company
                <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                <Underline />
              </button>
              <div className="invisible absolute left-0 top-full translate-y-2 pt-5 opacity-0 transition-[opacity,transform,visibility] duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div className="min-w-[220px] border border-ink/10 bg-white text-ink shadow-2xl">
                  <Link
                    to="/about"
                    data-testid="nav-about"
                    className="block border-b border-ink/5 px-6 py-4 text-[13px] font-medium uppercase tracking-[0.15em] transition-colors duration-200 hover:bg-paper hover:text-brand"
                  >
                    About Us
                  </Link>
                  <Link
                    to="/clientele"
                    data-testid="nav-clientele"
                    className="block px-6 py-4 text-[13px] font-medium uppercase tracking-[0.15em] transition-colors duration-200 hover:bg-paper hover:text-brand"
                  >
                    Clientele
                  </Link>
                </div>
              </div>
            </div>
            <Link to="/catalog" data-testid="nav-catalog" className={`${linkClass} ${pathname === "/catalog" ? "text-brand" : ""}`}>
              Catalog
              <Underline />
            </Link>
            <Link to="/contact" data-testid="nav-contact" className={`${linkClass} ${pathname === "/contact" ? "text-brand" : ""}`}>
              Contact Us
              <Underline />
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink text-white"
          >
            <div className="flex h-20 items-center justify-between px-6 sm:px-10">
              <span className="flex items-center gap-3">
                <img src="/assets/logo-mark.png" alt="Devkii Electricals logo" className="h-9 w-auto rounded-md" />
                <span className="font-heading text-lg font-bold tracking-tight">Devkii Electricals</span>
              </span>
              <button data-testid="nav-mobile-close" onClick={() => setOpen(false)} aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6 sm:px-10">
              {[
                { label: "Home", testId: "nav-mobile-home", to: "/" },
                { label: "About Us", testId: "nav-mobile-about", sub: true, to: "/about" },
                { label: "Clientele", testId: "nav-mobile-clientele", sub: true, to: "/clientele" },
                { label: "Catalog", testId: "nav-mobile-catalog", to: "/catalog" },
                { label: "Contact Us", testId: "nav-mobile-contact", to: "/contact" },
              ].map((item, i) => (
                <motion.a
                  key={item.testId}
                  href={item.to || "#"}
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.to) {
                      setOpen(false);
                      navigate(item.to);
                    }
                  }}
                  data-testid={item.testId}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className={`border-b border-white/10 py-5 font-heading font-bold tracking-tight transition-colors hover:text-brand ${
                    item.sub ? "pl-8 text-2xl text-white/60" : "text-4xl"
                  }`}
                >
                  {item.sub && <span className="mr-4 text-xs uppercase tracking-[0.3em] text-brand">Company</span>}
                  {item.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
